import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { projectsService, Project } from '@/services/projects.service';
import { clientsService, Client } from '@/services/clients.service';
import { mockTasks, mockTimeEntries } from '@/mocks/data';
import { Link } from 'react-router-dom';

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const projectData = await projectsService.getProject(id);
      setProject(projectData);

      // Load client data if it's a client project
      if (projectData.clientId) {
        try {
          const clientData = await clientsService.getClient(projectData.clientId);
          setClient(clientData);
        } catch (err) {
          console.error('Error loading client:', err);
        }
      }
    } catch (err: any) {
      console.error('Error loading project:', err);
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      setDeleting(true);
      await projectsService.deleteProject(id);
      navigate('/projects');
    } catch (err: any) {
      console.error('Error deleting project:', err);
      setError(err.message || 'Failed to delete project');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-flowky-primary"></div>
          <p className="text-gray-600 mt-4">Loading project details...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !project) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {error ? 'Error loading project' : 'Project not found'}
          </h2>
          {error && <p className="text-red-600 mb-4">{error}</p>}
          <Link to="/projects" className="text-flowky-primary hover:underline">
            Back to projects
          </Link>
        </div>
      </AppLayout>
    );
  }

  // Calculate stats from mock data (until we have task/time APIs)
  const projectTasks = mockTasks.filter((task) => task.projectId === project.id);
  const projectTimeEntries = mockTimeEntries.filter((entry) => entry.projectId === project.id);
  const totalHours = projectTimeEntries.reduce((acc, entry) => acc + entry.duration / 3600, 0);
  const totalEarnings = totalHours * (client?.hourlyRate || 0);
  const activeTasks = projectTasks.filter((task) => task.status !== 'completed').length;
  const completedTasks = projectTasks.filter((task) => task.status === 'completed').length;

  return (
    <AppLayout>
      <Breadcrumb
        items={[
          { label: 'Projects', href: '/projects' },
          { label: project.name },
        ]}
      />

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl ${
            project.type === 'client' ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-purple-500 to-purple-700'
          }`}>
            {project.name.charAt(0)}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-1 text-xs rounded-full ${
                project.type === 'client'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {project.type === 'client' ? 'Client Project' : 'Side Project'}
              </span>
              {client && (
                <Link
                  to={`/clients/${client.id}`}
                  className="text-sm text-gray-600 hover:text-flowky-primary"
                >
                  {client.name}
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Link
            to={`/projects/${project.id}/edit`}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors"
          >
            Edit
          </Link>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="border border-border rounded-lg p-4 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Project</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be
              undone and will remove all associated data.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* End Delete Modal */}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Tasks</p>
          <p className="text-2xl font-bold text-gray-900">{projectTasks.length}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Active Tasks</p>
          <p className="text-2xl font-bold text-blue-600">{activeTasks}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Hours Tracked</p>
          <p className="text-2xl font-bold text-green-600">{totalHours.toFixed(1)}h</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Earnings</p>
          <p className="text-2xl font-bold text-green-600">€{Math.round(totalEarnings)}</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Project Info */}
          <div className="border border-border rounded-lg p-4">
            <h2 className="text-lg font-bold mb-4">Project Information</h2>
            <div className="space-y-3">
              {client && (
                <div>
                  <p className="text-sm text-gray-600">Client</p>
                  <Link
                    to={`/clients/${client.id}`}
                    className="font-medium text-flowky-primary hover:underline"
                  >
                    {client.name}
                  </Link>
                </div>
              )}
              {project.notes && (
                <div>
                  <p className="text-sm text-gray-600">Description</p>
                  <p className="text-gray-700">{project.notes}</p>
                </div>
              )}
              {project.createdAt && (
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium">
                    {new Date(project.createdAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Tasks */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Tasks</h2>
              <div className="flex gap-4 text-sm">
                <span className="text-gray-600">
                  Active: <span className="font-semibold">{activeTasks}</span>
                </span>
                <span className="text-gray-600">
                  Completed: <span className="font-semibold">{completedTasks}</span>
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {projectTasks.slice(0, 5).map((task) => (
                <div
                  key={task.id}
                  className="p-3 rounded-lg hover:bg-white/30 transition-colors flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-900">{task.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {task.status === 'completed' ? 'Completed' : `Priority: ${task.priority}`}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      task.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {task.status === 'in-progress' && 'In Progress'}
                    {task.status === 'not-started' && 'Not Started'}
                    {task.status === 'completed' && 'Completed'}
                  </span>
                </div>
              ))}
              {projectTasks.length === 0 && <p className="text-sm text-gray-500">No tasks yet</p>}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
